'use client'

import { useTranslations } from 'next-intl'
import DateTimeField from '@/kmsApp/components/forms/datetime-picker'
import { Field } from '@/kmsApp/components/forms/field'
import { Control, FieldErrors, UseFormRegister, useWatch } from 'react-hook-form'
import { CourseForm as CourseFormType } from '@/kmsApp/types/forms/course-form'

interface Props {
  control: Control<CourseFormType>
  register: UseFormRegister<CourseFormType>
  errors: FieldErrors<CourseFormType>
  /** Date de depart : portee par le premier arret dans la saisie d'itineraire. */
  showDepart?: boolean
  /** Trajet retour : porte par le dernier arret dans la saisie d'itineraire. */
  showRetour?: boolean
}

/**
 * Dates de la course : depart, et retour optionnel.
 *
 * Extrait de {@link StopItem} parce que ces champs ne dependent pas des arrets :
 * sur un trajet preconfigure l'itineraire n'est pas saisi, mais la date reste
 * indispensable — sans ce composant elle n'aurait aucun champ ou s'ecrire.
 */
export const CourseDatesFields = ({
  control,
  register,
  errors,
  showDepart = false,
  showRetour = false,
}: Props) => {
  const t = useTranslations('web_course_form')
  const avecTrajetRetour = useWatch({ control, name: 'avecTrajetRetour' })
  const dateCourse = useWatch({ control, name: 'dateCourse' })
  const dateTrajetRetour = useWatch({ control, name: 'dateTrajetRetour' })

  return (
    <>
      {showDepart && (
        <div className="pt-4 mt-2">
          <DateTimeField
            control={control}
            titre={t('departure_date')}
            error={errors.dateCourse?.message}
            {...register('dateCourse', {
              required: t('departure_date_required'),
            })}
            containerClassName="w-full"
          />
        </div>
      )}

      {showRetour && (
        <div className="pt-4 mt-2 grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <Field
            titre={t('add_return_trip')}
            type="checkbox"
            {...register('avecTrajetRetour')}
          />
          {avecTrajetRetour && (
            <DateTimeField
              control={control}
              titre={t('return_date')}
              {...register('dateTrajetRetour', {
                required: t('return_date_required'),
              })}
              minDate={dateCourse ? new Date(dateCourse) : undefined}
              error={
                dateTrajetRetour && dateCourse && dateTrajetRetour < dateCourse
                  ? t('return_after_departure')
                  : errors.dateTrajetRetour?.message
              }
            />
          )}
        </div>
      )}
    </>
  )
}
